import { Overlay, OverlayConfig, OverlayContainer, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType, TemplatePortal } from '@angular/cdk/portal';
import {
    ApplicationRef,
    ElementRef,
    Inject,
    Injectable,
    InjectionToken,
    Injector,
    OnDestroy,
    Optional,
    SkipSelf,
    StaticProvider,
    TemplateRef
} from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { ModalBottomContainerComponent } from '@df/ui/modal/modal-bottom-container.component';
import { ModalConfig, TModalPosition } from '@df/ui/modal/modal-config';
import { ModalContainer } from '@df/ui/modal/modal-container';
import { ModalContainerComponent } from '@df/ui/modal/modal-container.component';
import { ModalCustomContainerComponent } from '@df/ui/modal/modal-custom-container.component';
import { ModalOverlay } from '@df/ui/modal/modal-overlay';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalRightContainerComponent } from '@df/ui/modal/modal-right-container.component';
import { ModalTopContainerComponent } from '@df/ui/modal/modal-top-container.component';
import { Observable, Subject, Subscription, defer } from 'rxjs';
import { startWith } from 'rxjs/operators';

/** Injection token that can be used to access the data that was passed in to a modal. */
export const MODAL_DATA = new InjectionToken<any>('ModalData');

/** Injection token that can be used to specify default modal options. */
export const MODAL_DEFAULT_OPTIONS = new InjectionToken<ModalConfig>('modal-default-options');

/** Injection token that determines the scroll handling while the modal is open. */
export const MODAL_SCROLL_STRATEGY = new InjectionToken<() => ScrollStrategy>('modal-scroll-strategy');

export function MODAL_SCROLL_STRATEGY_FACTORY(overlay: ModalOverlay): () => ScrollStrategy {
    return () => overlay.scrollStrategies.block();
}

export function MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay: ModalOverlay): () => ScrollStrategy {
    return () => overlay.scrollStrategies.block();
}

export const MODAL_SCROLL_STRATEGY_PROVIDER = {
    provide: MODAL_SCROLL_STRATEGY,
    deps: [ModalOverlay],
    useFactory: MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY
};

/**
 * Modal service
 *
 * @example
 * ```typescript
 * this.modalService.open(ExampleComponent, {});
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class ModalService implements OnDestroy {
    protected openModalsAtThisLevel: ModalRef<any>[] = [];
    protected readonly afterAllClosedAtThisLevel = new Subject<void>();
    protected readonly afterOpenedAtThisLevel = new Subject<ModalRef<any>>();
    readonly modalOpened$ = new Subject<ModalRef<any>>();
    protected ariaHiddenElements = new Map<Element, string | null>();
    protected scrollStrategy: () => ScrollStrategy;

    /** Keeps track of the currently-open modals. */
    get openModals(): ModalRef<any>[] {
        return this.parentModal ? this.parentModal.openModals : this.openModalsAtThisLevel;
    }

    /** Stream that emits when a modal has been opened. */
    get afterOpened(): Subject<ModalRef<any>> {
        return this.parentModal ? this.parentModal.afterOpened : this.afterOpenedAtThisLevel;
    }

    get _afterAllClosed(): Subject<void> {
        const parent = this.parentModal;
        return parent ? parent._afterAllClosed : this.afterAllClosedAtThisLevel;
    }

    /**
     * Stream that emits when all open modal have finished closing.
     * Will emit on subscribe if there are no open modals to begin with.
     */
    readonly afterAllClosed: Observable<void> = defer(() =>
        this.openModals.length ? this._afterAllClosed : this._afterAllClosed.pipe(startWith(undefined))
    ) as Observable<any>;

    constructor(
        protected overlay: Overlay,
        protected injector: Injector,
        protected meta: Meta,
        @Optional() @Inject(MODAL_DEFAULT_OPTIONS) protected defaultOptions: ModalConfig,
        @Inject(MODAL_SCROLL_STRATEGY) scrollStrategy: any,
        @Optional() @SkipSelf() protected parentModal: ModalService,
        protected overlayContainer: OverlayContainer,
        protected router: Router,
        private app: App,
        private applicationRef: ApplicationRef
    ) {
        this.scrollStrategy = scrollStrategy;
    }

    /**
     * Opens a modal modal containing the given component.
     * @param componentOrTemplateRef Type of the component to load into the modal,
     *     or a TemplateRef to instantiate as the modal content.
     * @param config Extra configuration options.
     * @returns Reference to the newly-opened modal.
     */
    open<T, D = any, R = any>(
        componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
        config?: ModalConfig<D>,
        position?: TModalPosition,
        onCreate?: (ref: ModalRef<T, R>) => void
    ): ModalRef<T, R> {
        config = applyConfigDefaults(config, this.defaultOptions || new ModalConfig());

        if (config.id && this.getModalById(config.id)) {
            throw Error(`Modal with id "${config.id}" exists already. The modal id must be unique.`);
        }
        const overlayRef = this.createOverlay(config);
        const modalContainerComponent = this.attachModalContainer(overlayRef, config, position);
        const modalRef = this.attachModalContent<T, R>(componentOrTemplateRef, modalContainerComponent, overlayRef, config);

        // If this is the first modal that we're opening, hide all the non-overlay content.
        if (!this.openModals.length) {
            this.hideNonModalContentFromAssistiveTechnology();
        }

        let routerSubscription: Subscription;

        if (config.closeOnRouterStart || config.closeOnNavigation) {
            const onEvent = (event: Event) => {
                // we need navigation end in case when modal is opened after navigation start but before navigation end
                if (event instanceof NavigationStart || event instanceof NavigationEnd) {
                    modalRef.close();
                }
            };

            routerSubscription = config.closeOnRouterStart
                ? this.router.events.subscribe(onEvent)
                : this.meta.routerEvents.subscribe(onEvent);
        }

        this.openModals.push(modalRef);

        modalRef.afterClosed().subscribe(() => {
            this.removeOpenModal(modalRef);

            if (routerSubscription) {
                routerSubscription.unsubscribe();
            }

            if (config?.onClose) {
                config.onClose();
            }
        });

        this.afterOpened.next(modalRef);
        this.modalOpened$.next(modalRef);

        if (config.onOpen) {
            config.onOpen();
        }
        if (onCreate) {
            onCreate(modalRef);
        }

        // Notify the dialog container that the content has been attached.
        modalContainerComponent.initializeWithAttachedContent();

        if (this.app?.ngZoneOff) {
            this.applicationRef.tick();
        }

        return modalRef;
    }

    openBottom<T, D = any, R = any>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>, config?: ModalConfig<D>): ModalRef<T, R> {
        const _config = {
            ...config,
            ...{
                maxWidth: '100%',
                position: {
                    bottom: '0'
                }
            }
        };

        return this.open(componentOrTemplateRef, _config, 'bottom');
    }

    openRight<T, D = any, R = any>(componentOrTemplateRef: ComponentType<T> | TemplateRef<T>, config?: ModalConfig<D>): ModalRef<T, R> {
        const _config = {
            ...config,
            ...{
                maxHeight: '100%',
                position: {
                    right: '0'
                }
            }
        };

        return this.open(componentOrTemplateRef, _config, 'right');
    }

    /**
     * Closes all of the currently-open modals.
     */
    closeAll(): void {
        this.closeModals(this.openModals);
    }

    /**
     * Closes all modals except the ones with specified IDs.
     * @param modalIds The IDs of modals that should not be closed.
     */
    closeAllExcept(modalIds: string[]): void {
        const modalsToClose = this.openModals.filter(modal => {
            const modalId = modal?.componentInstance?.modalConfig?.id;
            return !modalIds.includes(modalId);
        });

        this.closeModals(modalsToClose);
    }

    /**
     * Finds an open modal by its id.
     * @param id ID to use when looking up the modal.
     */
    getModalById(id: string): ModalRef<any> | undefined {
        return this.openModals.find(modal => modal.id === id);
    }

    ngOnDestroy(): void {
        // Only close the modals at this level on destroy
        // since the parent service may still be active.
        this.closeModals(this.openModalsAtThisLevel);
        this.afterAllClosedAtThisLevel.complete();
        this.afterOpenedAtThisLevel.complete();
    }

    /**
     * Creates the overlay into which the modal will be loaded.
     * @param config The modal configuration.
     * @returns A promise resolving to the OverlayRef for the created overlay.
     */
    protected createOverlay(config: ModalConfig): OverlayRef {
        const overlayConfig = this.getOverlayConfig(config);

        return this.overlay.create(overlayConfig);
    }

    /**
     * Creates an overlay config from a modal config.
     * @param modalConfig The modal configuration.
     * @returns The overlay configuration.
     */
    protected getOverlayConfig(modalConfig: ModalConfig): OverlayConfig {
        if (typeof modalConfig.scrollStrategy === 'string') {
            switch (modalConfig.scrollStrategy) {
                case 'block':
                    modalConfig.scrollStrategy = this.overlay.scrollStrategies.block();
                    break;
                case 'close':
                    modalConfig.scrollStrategy = this.overlay.scrollStrategies.close();
                    break;
                case 'reposition':
                    modalConfig.scrollStrategy = this.overlay.scrollStrategies.reposition();
                    break;
                default:
                    modalConfig.scrollStrategy = this.overlay.scrollStrategies.noop();
                    break;
            }
        }

        const state = new OverlayConfig({
            positionStrategy: this.overlay.position().global(),
            scrollStrategy: modalConfig.scrollStrategy || this.scrollStrategy(),
            panelClass: modalConfig.panelClass,
            hasBackdrop: modalConfig.hasBackdrop,
            width: modalConfig.width,
            height: modalConfig.height,
            minWidth: modalConfig.minWidth,
            minHeight: modalConfig.minHeight,
            maxWidth: modalConfig.maxWidth,
            maxHeight: modalConfig.maxHeight,
            usePopover: modalConfig.usePopover ?? false
        } as any); // popover is only in newer versions, for legacy compatibility we use as any

        if (modalConfig.backdropClass) {
            state.backdropClass = modalConfig.backdropClass;
        }

        return state;
    }

    /**
     * Attaches an ModalContainer to a modal's already-created overlay.
     * @param overlay Reference to the modal's underlying overlay.
     * @param config The modal configuration.
     * @returns A promise resolving to a ComponentRef for the attached container.
     */
    protected attachModalContainer(overlay: OverlayRef, config: ModalConfig, position?: TModalPosition): ModalContainer {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        const injector = Injector.create({
            parent: userInjector || this.injector,
            providers: [{ provide: ModalConfig, useValue: config }]
        });

        let container = ModalContainerComponent;

        switch (position) {
            case 'top':
                container = ModalTopContainerComponent;
                break;
            case 'right':
                container = ModalRightContainerComponent;
                break;
            case 'bottom':
                container = ModalBottomContainerComponent;
                break;
            case 'custom':
                container = ModalCustomContainerComponent;
                break;
        }

        const containerPortal = new ComponentPortal(container, config.viewContainerRef, injector);

        const containerRef = overlay.attach<ModalContainer>(containerPortal);

        return containerRef.instance;
    }

    /**
     * Attaches the user-provided component to the already-created ModalContainer.
     * @param componentOrTemplateRef The type of component being loaded into the modal,
     *     or a TemplateRef to instantiate as the content.
     * @param modalContainerComponent Reference to the wrapping ModalContainer.
     * @param overlayRef Reference to the overlay in which the modal resides.
     * @param config The modal configuration.
     * @returns A promise resolving to the ModalRef that should be returned to the user.
     */
    protected attachModalContent<T, R>(
        componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
        modalContainerComponent: ModalContainer,
        overlayRef: OverlayRef,
        config: ModalConfig
    ): ModalRef<T, R> {
        // Create a reference to the modal we're creating in order to give the user a handle
        // to modify and close it.
        const modalRef = new ModalRef<T, R>(overlayRef, modalContainerComponent, config.id);

        // When the modal backdrop is clicked, we want to close it.
        if (config.hasBackdrop) {
            overlayRef.backdropClick().subscribe(() => {
                if (modalRef.onBackdropClick) {
                    modalRef.onBackdropClick();
                }

                if (!modalRef.disableClose) {
                    modalRef.close();
                }
            });
        }

        if (componentOrTemplateRef instanceof TemplateRef) {
            modalContainerComponent.attachTemplatePortal(
                // tslint:disable-next-line: no-non-null-assertion
                new TemplatePortal<T>(componentOrTemplateRef, null!, <any>{ $implicit: config.data, modalRef })
            );
        } else {
            const injector = this.createInjector<T>(config, modalRef, modalContainerComponent);
            const contentRef = modalContainerComponent.attachComponentPortal<T>(
                new ComponentPortal(componentOrTemplateRef, undefined, injector)
            );
            modalRef.componentInstance = contentRef.instance;
        }

        modalRef.updatePosition(config.position);

        return modalRef;
    }

    /**
     * Creates a custom injector to be used inside the modal. This allows a component loaded inside
     * of a modal to close itself and, optionally, to return a value.
     * @param config Config object that is used to construct the modal.
     * @param modalRef Reference to the modal.
     * @param container Modal container element that wraps all of the contents.
     * @returns The custom injector that can be used inside the modal.
     */
    protected createInjector<T>(config: ModalConfig, modalRef: ModalRef<T>, modalContainerComponent: ModalContainer): Injector {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

        // The ModalContainer is injected in the portal as the ModalContainer and the modal's
        // content are created out of the same ViewContainerRef and as such, are siblings for injector
        // purposes. To allow the hierarchy that is expected, the ModalContainer is explicitly
        // added to the injection tokens.
        const providers: StaticProvider[] = [
            { provide: ModalContainerComponent, useValue: modalContainerComponent },
            { provide: MODAL_DATA, useValue: config.data },
            { provide: ModalRef, useValue: modalRef }
        ];

        return Injector.create({ parent: userInjector || this.injector, providers });
    }

    /**
     * Removes a modal from the array of open modals.
     * @param modalRef Modal to be removed.
     */
    protected removeOpenModal(modalRef: ModalRef<any>): void {
        const index = this.openModals.indexOf(modalRef);

        if (index > -1) {
            this.openModals.splice(index, 1);

            // If all the modals were closed, remove/restore `inert` on the
            // siblings and emit to the `afterAllClosed` stream.
            if (!this.openModals.length) {
                this.ariaHiddenElements.forEach((previousValue, element) => {
                    if (previousValue !== null) {
                        element.setAttribute('inert', previousValue);
                    } else {
                        element.removeAttribute('inert');
                    }
                });

                this.ariaHiddenElements.clear();
                this._afterAllClosed.next();
            }
        }
    }

    /**
     * Hides all of the content that isn't an overlay from assistive technology.
     */
    protected hideNonModalContentFromAssistiveTechnology(): void {
        const overlayContainer = this.overlayContainer.getContainerElement();

        // Ensure that the overlay container is attached to the DOM.
        if (overlayContainer.parentElement) {
            const siblings = overlayContainer.parentElement.children;

            for (let i = siblings.length - 1; i > -1; i--) {
                const sibling = siblings[i];

                if (
                    sibling !== overlayContainer &&
                    sibling.nodeName !== 'SCRIPT' &&
                    sibling.nodeName !== 'STYLE' &&
                    !sibling.hasAttribute('aria-live')
                ) {
                    this.ariaHiddenElements.set(sibling, sibling.getAttribute('inert'));
                    sibling.setAttribute('inert', '');
                }
            }
        }
    }

    /** Closes all of the modals in an array. */
    protected closeModals(modals: ModalRef<any>[]): void {
        let i = modals.length;

        try {
            while (i--) {
                // The `_openModals` property isn't updated after close until the rxjs subscription
                // runs on the next microtask, in addition to modifying the array as we're going
                // through it. We loop through all of them and call close without assuming that
                // they'll be removed from the list instantaneously.
                modals[i]?.close();
            }
        } catch (e) {
            console.error(e);
        }
    }
}

/**
 * Applies default options to the modal config.
 * @param config Config to be modified.
 * @param defaultOptions Default options provided.
 * @returns The new configuration object.
 */
function applyConfigDefaults(config?: ModalConfig, defaultOptions?: ModalConfig): ModalConfig {
    return { ...defaultOptions, ...config };
}

/**
 * Finds the closest ModalRef to an element by looking at the DOM.
 * @param element Element relative to which to look for a dialog.
 * @param openModals References to the currently-open modals.
 */
export function getClosestModal(element: ElementRef<HTMLElement>, openModals: ModalRef<any>[]): ModalRef<any, any> | null | undefined {
    let parent: HTMLElement | null = element.nativeElement.parentElement;

    while (parent && !parent.classList.contains('modal-container')) {
        parent = parent.parentElement;
    }

    // tslint:disable-next-line: no-non-null-assertion
    return parent ? openModals.find(dialog => dialog.id === parent!.id) : null;
}
